// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/OnboardingV2Page.tsx (L286-314, the assessment artifact button)
// ADAPTED (plan F14, P3-12, P4-1): TP's button, inline under the agent's turn, for "Build my assessment". TP's button opens
// a finished Growth Score; ours marks the onboarding complete (`onBuild`, a local "complete" mark) and then opens the
// Growth Score page, /onboarding/assessment (phase 4), which the BFF builds. It has a disabled state (ds-disabled colours,
// never opacity) while the BFF has not stored the quiz answers, and the same click guard as the docked step it replaces.
import { ChevronRight, Gauge } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { ASSESSMENT_PATH } from '@/features/assessment/paths';
import { cn } from '@/lib/utils';

import { COPY } from '../copy';
import { PANEL_CLICK_GUARD_MS, useClickGuard } from '../hooks/useClickGuard';

export interface BuildCardProps {
  brand: string;
  /** The BFF holds the quiz answers; until then the card cannot be pressed and says why. */
  ready: boolean;
  onBuild: () => void;
  /** Clicks are ignored this long after the card mounts. Tests turn it off with 0. */
  clickGuardMs?: number;
  /**
   * The card has just taken the place of the competitors step the person was on, so keyboard focus (which left with
   * that step) moves here, as a new step does elsewhere in the chat. Never steals focus from something else the
   * person has moved to, and a card drawn with a resumed chat does not ask for it.
   */
  focusOnArrival?: boolean;
}

export function BuildCard({ brand, ready, onBuild, clickGuardMs = PANEL_CLICK_GUARD_MS, focusOnArrival = false }: BuildCardProps) {
  const guard = useClickGuard(clickGuardMs);
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLButtonElement>(null);
  // A disabled button cannot take focus, so this waits for `ready` (the answers are stored).
  useEffect(() => {
    if (!focusOnArrival || !ready) return;
    const active = document.activeElement;
    if (active === null || active === document.body) buttonRef.current?.focus({ preventScroll: true });
  }, [focusOnArrival, ready]);
  return (
    <div data-testid="build-card" data-ready={ready ? 'true' : 'false'} className="flex w-full flex-col items-start">
      <button
        ref={buttonRef}
        type="button"
        disabled={!ready}
        onClick={guard(() => {
          // Flushed first, so the "complete" mark is written to the stored session before this page is left.
          flushSync(onBuild);
          navigate(ASSESSMENT_PATH);
        })}
        aria-label={`${COPY.finishButton}: ${brand}`}
        className={cn(
          'flex w-full max-w-md items-center gap-3 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-focus',
          ready
            ? 'border-ds-hairline bg-card hover:bg-ds-ink-a05'
            : 'cursor-not-allowed border-ds-disabled-border bg-ds-disabled-bg text-ds-disabled-text',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-md',
            ready ? 'bg-ds-ink-a05 text-ds-text-2' : 'bg-ds-ink-a05 text-ds-disabled-text',
          )}
        >
          <Gauge className="size-4" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className={cn('truncate text-tab', ready ? 'text-foreground' : 'text-ds-disabled-text')}>{`${brand} onboarding`}</span>
          <span className={cn('text-label', ready ? 'text-ds-text-2' : 'text-ds-disabled-text')}>{COPY.finishCardSub}</span>
        </span>
        <span className={cn('flex shrink-0 items-center gap-1 text-tab', ready ? 'text-foreground' : 'text-ds-disabled-text')}>
          {COPY.finishButton}
          <ChevronRight aria-hidden className={cn('size-4', ready ? 'text-ds-text-3' : 'text-ds-disabled-text')} />
        </span>
      </button>
      {/* Always in the DOM, so the "Saving…" text is announced when it appears and the card does not jump. */}
      <span role="status" className={cn('text-label text-ds-text-2', !ready && 'mt-1.5')}>
        {ready ? '' : COPY.savingAnswers}
      </span>
    </div>
  );
}
